<template>
  <div class="news-list">
    <section class="about-content">
      <div class="container">
        <div class="about-header" ref="headerSection" :class="{ 'fade-in': isHeaderVisible }">
          <div class="title-box">
            <h1>新闻动态</h1>
            <p>了解美音最新动态</p>
          </div>
          <div class="title-border"></div>
        </div>

        <div class="news-grid" ref="newsSection" :class="{ 'fade-in': isNewsVisible }">
          <div 
            v-for="news in newsList" 
            :key="news.id"
            class="news-item"
            @click="goToDetail(news.id)"
          >
            <div class="news-image">
              <img :src="baseUrl + '/media' + news.cover" :alt="news.title">
              <div class="image-overlay">
                <div class="view-more">
                  <span>查看详情</span>
                  <el-icon><ArrowRight /></el-icon>
                </div>
              </div>
            </div>
            <div class="news-content">
              <h3>{{ news.title }}</h3>
              <p>{{ stripHtml(news.content) }}</p>
              <div class="news-meta">
                <span class="date">{{ formatDate(news.publish_time) }}</span>
                <span class="views">{{ news.views }} 阅读</span>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination">
          <el-pagination
            v-model:current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            layout="prev, pager, next"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowRight } from '@element-plus/icons-vue'
import axios from 'axios'
import { getBaseApiUrl } from '@/utils/api-config'

const router = useRouter()
const baseUrl = getBaseApiUrl()
const currentPage = ref(1)
const pageSize = ref(9)
const total = ref(0)
const newsList = ref([])

// 获取新闻列表数据
const getNewsList = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/article/article/`, {
      params: {
        page: currentPage.value,
        limit: pageSize.value,
        column: 7,
        status: 'published'
      }
    })
    if (response.data.code === 2000) {
      newsList.value = response.data.data.data
      total.value = response.data.data.total
    }
  } catch (error) {
    console.error('获取新闻列表失败:', error)
  }
}

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// 去除 HTML 标签
const stripHtml = (html) => {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

const isHeaderVisible = ref(false)
const headerSection = ref(null)
const isNewsVisible = ref(false)
const newsSection = ref(null)

const goToDetail = (id) => {
  router.push(`/news/${id}`)
}

const handlePageChange = (page) => {
  currentPage.value = page
  getNewsList()
}

onMounted(() => {
  getNewsList()
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.target === headerSection.value) {
        isHeaderVisible.value = entry.isIntersecting
      }
      if (entry.target === newsSection.value) {
        isNewsVisible.value = entry.isIntersecting
      }
    })
  }, {
    threshold: 0.2
  })

  if (headerSection.value) {
    observer.observe(headerSection.value)
  }
  if (newsSection.value) {
    observer.observe(newsSection.value)
  }

  return () => observer.disconnect()
})
</script>

<style lang="scss" scoped>
.news-list {
  .about-content {
    padding: 60px 0;
    background: #fff;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;

      .about-header {
        // 复用解决方案页面的样式
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 40px;
        padding-bottom: 20px;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);

        &.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        // ... 其他header样式
      }

      .news-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 30px;
        opacity: 0;
        transform: translateY(30px);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        .news-item {
          background: #fff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

            .news-image {
              img {
                transform: scale(1.05);
              }

              .image-overlay {
                opacity: 1;

                .view-more {
                  transform: translateY(0);
                }
              }
            }
          }

          .news-image {
            position: relative;
            height: 200px;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              transition: transform 0.5s;
            }

            .image-overlay {
              position: absolute;
              inset: 0;
              background: linear-gradient(to bottom,
                rgba(0, 0, 0, 0) 0%,
                rgba(0, 0, 0, 0.6) 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0;
              transition: all 0.3s;

              .view-more {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 12px 24px;
                background: rgba(255, 255, 255, 0.9);
                border-radius: 25px;
                transform: translateY(20px);
                transition: all 0.3s;
                font-size: 15px;
                font-weight: 500;
                color: #2b5dff;

                .el-icon {
                  transition: transform 0.3s;
                }

                &:hover {
                  background: #2b5dff;
                  color: #fff;

                  .el-icon {
                    transform: translateX(4px);
                  }
                }
              }
            }
          }

          .news-content {
            padding: 20px;

            h3 {
              font-size: 18px;
              color: #333;
              margin-bottom: 12px;
              line-height: 1.4;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            p {
              font-size: 14px;
              color: #666;
              line-height: 1.6;
              margin-bottom: 16px;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            .news-meta {
              display: flex;
              justify-content: space-between;
              color: #999;
              font-size: 13px;
            }
          }
        }
      }

      .pagination {
        margin-top: 40px;
        display: flex;
        justify-content: center;
      }
    }
  }

  @media (max-width: 992px) {
    .news-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }

  @media (max-width: 768px) {
    .news-grid {
      grid-template-columns: 1fr !important;
    }
  }
}
</style> 